/**
 * "Bekijken als": a developer views the whole dashboard as one colleague or
 * as one role, read-only, until they stop. The backend decides everything
 * (who may, what a view shows, that nothing can change while it is on);
 * `/auth/me` then answers as the viewed person and carries `view_as`, and the
 * shell pins a bar above every page (`<ViewAsBar>`). The MCP connector never
 * sees a view: it only changes the dashboard.
 *
 * Every start, switch and stop loads the dashboard again. The bar then says
 * whom it shows, or is gone, so no toast repeats what the developer just did.
 * The API client (`src/lib/api/client.ts`) toasts what the developer cannot
 * see otherwise: a change refused while viewing, and a view that ended or
 * changed in another tab.
 */

import type { AppSummary, StartViewAs, UserProfile, ViewAsState } from "@/lib/api";
import {
  accountInitial,
  accountName,
  fullName,
  isNavItemCurrent,
  VIEW_AS_PATH,
} from "@/lib/navigation";
import { roleLabel } from "@/lib/roles";

/** The roles a developer can view the dashboard as, in the order the dashboard lists them. */
export const VIEW_AS_ROLES = ["user", "admin", "owner"] as const;

export type ViewAsRole = (typeof VIEW_AS_ROLES)[number];

export function isViewAsRole(value: unknown): value is ViewAsRole {
  return typeof value === "string" && (VIEW_AS_ROLES as readonly string[]).includes(value);
}

/**
 * The shell's classes while the bar shows, as full literal classes so
 * Tailwind generates them. The bar is 44 px high and sticks to the top of
 * the window; the sidebar's column and the phone's top bar stick right under
 * it, the sidebar 44 px shorter than the window, and the shell 44 px shorter
 * too, so a short page never scrolls. `--shell-top-inset` tells what sticks
 * inside the page (the Bezettingsgraad app's control bar) where the free
 * space starts. The toasts start below the bar too (`--toast-bar-inset` in
 * `src/styles/globals.css`, which repeats its 44 px). In px, never rem: the
 * root font size here is 18 px.
 */
export const VIEW_AS_LAYOUT = {
  bar: "h-[44px]",
  shell: "min-h-[calc(100vh-44px)] [--shell-top-inset:44px]",
  stickyTop: "top-[44px]",
  sidebarHeight: "h-[calc(100vh-44px)]",
  /**
   * A ghost `<Button>` on the bar's blue, 36 px high: its icon and its name,
   * and on a phone its icon alone in a 40 px square, the name left to screen
   * readers.
   */
  button:
    "text-primary-foreground hover:bg-primary-foreground/15 hover:text-primary-foreground focus-visible:ring-primary-foreground focus-visible:ring-offset-primary data-[state=open]:bg-primary-foreground/15 h-8 shrink-0 gap-1.5 px-2.5 py-0 max-sm:size-9 max-sm:px-0",
} as const;

/** Who a view shows, in words: the person's name, else their email; a role by its label. */
export function viewAsName(view: ViewAsState): string {
  if (view.kind === "role") {
    return roleLabel(view.role);
  }
  return fullName(view) ?? view.email ?? roleLabel(view.role);
}

/** Whether a view is the one a choice would start: the same person, or the same role. */
export function isCurrentView(view: ViewAsState | null | undefined, target: StartViewAs): boolean {
  if (!view) {
    return false;
  }
  if (view.kind === "role") {
    return !target.person_id && target.role === view.role;
  }
  return !!target.person_id && target.person_id === view.person_id;
}

/**
 * The name the account menu shows: the viewed person's while a view is on,
 * as `/auth/me` answers for them, and for a role view the role's label,
 * never its made-up address.
 */
export function shownAccountName(me: UserProfile): string {
  return me.view_as?.kind === "role" ? roleLabel(me.view_as.role) : accountName(me);
}

/** The account menu's avatar letter: the viewed person's, or the role label's first for a role view. */
export function shownAccountInitial(me: UserProfile): string {
  if (me.view_as?.kind === "role") {
    return (Array.from(roleLabel(me.view_as.role))[0] ?? "?").toUpperCase();
  }
  return accountInitial(me);
}

/** The email the account menu shows; none for a role view, whose address is made up. */
export function shownAccountEmail(me: UserProfile): string | null {
  return me.view_as?.kind === "role" ? null : me.email || null;
}

/** The account the persona switch of a local test stands for: always a developer while viewing. */
export function realRole(me: UserProfile): string {
  // Only a developer can view the dashboard as someone else.
  return me.view_as ? "developer" : me.role;
}

/**
 * Loads the dashboard again: at `path`, or where it is. A full load, so every
 * query starts afresh for the identity the server now answers as.
 */
export function reloadDashboard(path?: string): void {
  if (path) {
    window.location.assign(path);
  } else {
    window.location.reload();
  }
}

/**
 * Loads Settings → Bekijken als after Stoppen, whichever page it was on, now
 * as the developer.
 */
export function reloadAfterStop(): void {
  // The page the sidebar's Bekijken als entry opens, wherever its route goes.
  reloadDashboard(VIEW_AS_PATH);
}

/** Whether a path is an app's own page (`/apps/bezetting/kranen`), which says "Geen toegang" itself. */
export function isAppPage(pathname: string): boolean {
  return /^\/apps\/[^/]/.test(pathname);
}

/**
 * Where the dashboard loads again after Wissel: where it is (undefined), unless
 * that is an app page the new view may not open, then Home. `apps` are the
 * apps the new view may open. The admin and developer pages need no check:
 * their route guards send a view that may not open them to Home.
 */
export function pathAfterSwitch(
  pathname: string,
  apps: readonly Pick<AppSummary, "path">[],
): string | undefined {
  if (!isAppPage(pathname)) {
    return undefined;
  }
  return apps.some((app) => isNavItemCurrent({ to: app.path }, pathname)) ? undefined : "/home";
}
